<script setup>
import { useI18n } from 'vue-i18n';
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import AuthShell from '@/components/auth/AuthShell.vue';
import AuthField from '@/components/auth/AuthField.vue';
import UiButton from '@/components/ui/UiButton.vue';
import { useAuthStore } from '@/store/auth/index.js';
import { usePageMeta } from '@/composables/usePageMeta';
import { fieldErrors } from '@/utils/auth/password';
import { requestError } from '@/utils/study/labels';

const auth = useAuthStore();
const route = useRoute();
const { t, locale } = useI18n();

usePageMeta(() => ({ title: t('study.auth.forgot.metaTitle'), description: t('study.auth.forgot.metaDesc'), path: '/forgot-password', lang: locale.value }));

const email = ref(typeof route.query.email === 'string' ? route.query.email : '');
const loading = ref(false);
const sentTo = ref('');
const error = ref('');
const emailError = ref('');

async function submit() {
  if (loading.value) return;
  error.value = emailError.value = '';
  if (!/^\S+@\S+\.\S+$/.test(email.value.trim())) {
    emailError.value = t('study.auth.forgot.invalidEmail');
    return;
  }
  loading.value = true;
  try {
    await auth.forgotPassword(email.value);
    sentTo.value = email.value.trim();
  } catch (e) {
    emailError.value = fieldErrors(e).email ?? '';
    if (!emailError.value) error.value = requestError(e);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AuthShell
    :badge="t('study.auth.panelBadge')"
    :home-label="t('study.auth.backToSite')"
    :title="t('study.auth.forgot.title')"
    :accent="t('study.auth.forgot.accent')"
    :lead="t('study.auth.forgot.lead')"
    :card-title="sentTo ? t('study.auth.forgot.cardSent') : t('study.auth.forgot.cardLink')"
  >
    <div v-if="sentTo" class="space-y-6" role="status">
      <div class="rounded-2xl bg-deen-ink/[0.03] border border-deen-ink/5 p-5">
        <span class="w-12 h-12 rounded-2xl bg-deen-gold/40 flex items-center justify-center mb-4">
          <iconify-icon icon="solar:letter-opened-linear" class="text-2xl text-deen-ink" aria-hidden="true" />
        </span>
        <p class="text-sm leading-relaxed text-deen-ink/70">
          <i18n-t keypath="study.auth.forgot.sentBody" tag="span">
            <template #email><strong class="text-deen-ink break-all">{{ sentTo }}</strong></template>
          </i18n-t>
        </p>
        <p class="mt-3 text-xs text-deen-ink/45">{{ t('study.auth.forgot.sentHint') }}</p>
      </div>
      <div class="flex flex-wrap gap-3">
        <UiButton intent="secondary" @click="sentTo = ''">{{ t('study.auth.forgot.resend') }}</UiButton>
        <UiButton :to="{ name: 'admin.login', query: { email: sentTo } }" intent="ghost" icon="solar:arrow-left-linear">{{ t('study.auth.forgot.backToLogin') }}</UiButton>
      </div>
    </div>

    <form v-else class="space-y-5" novalidate @submit.prevent="submit">
      <div v-if="error" class="flex items-start gap-3 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800" role="alert">
        <iconify-icon icon="solar:danger-circle-linear" class="text-lg shrink-0 mt-px" aria-hidden="true" />{{ error }}
      </div>
      <AuthField v-model="email" :label="t('study.common.email')" type="email" autocomplete="username" :error="emailError" required />
      <UiButton type="submit" size="lg" block  :loading="loading">{{ t('study.auth.forgot.send') }}</UiButton>
      <p class="text-center">
        <RouterLink :to="{ name: 'admin.login' }" class="text-xs font-semibold text-deen-ink/60 hover:text-deen-ink underline-offset-4 hover:underline">{{ t('study.auth.forgot.remembered') }}</RouterLink>
      </p>
    </form>
  </AuthShell>
</template>
